import { useCallback, useEffect, useState } from 'react';
import { Alert } from 'components/atoms/Alert';
import { Stack } from 'components/base/layout/Stack';
import { Typography } from 'components/atoms/Typography';
import { Snackbar } from 'components/atoms/Snackbar';
import { EventBusyIcon } from 'assets/icons/EventBusyIcon';
import ConfirmationModal from '../../__v2__/ConfirmationModal';
import useGet from 'hooks/useGet';
import { useOrganisationPermission } from 'hooks/permissions/useOrganisationPermission';
import useUser from 'hooks/useUser';
import WarningModal from '../../__v5__/Modals/WarningModal/WarningModal';
import {
  formatDate,
  parseDate,
  startOfTime,
  differenceInTime,
} from 'utils/dateUtils';
import { TimeUnit } from 'types/date';
import { useStyle } from './style';
import { secondary } from 'assets/colors';

export const SubscriptionExpire = () => {
  const classes = useStyle();
  const { user, logout } = useUser();
  const token = localStorage.getItem('token');
  const tenant = localStorage.getItem('tenant');
  const [tenantId, setTenantId] = useState(tenant);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const [isSubscriptionExpiringModalOpen, setIsSubscriptionExpiringModalOpen] =
    useState(false);
  const [
    isSubscriptionGracePeriodModalOpen,
    setIsSubscriptionGracePeriodModalOpen,
  ] = useState(false);
  const [isSubscriptionExpiredModalOpen, setIsSubscriptionExpiredModalOpen] =
    useState(false);
  const [remainingDays, setRemainingDays] = useState(0);
  const { canManageOrganisationPermission } = useOrganisationPermission();

  const handleSubscriptionGracePeriodModalClose = useCallback(() => {
    setIsSubscriptionGracePeriodModalOpen(false);
  }, []);

  const handleSubscriptionStatus = useCallback(
    (data: {
      is_maintenance: boolean;
      is_server_terminated: boolean;
      expired_date: string;
    }) => {
      if (data) {
        setIsMaintenanceModalOpen(data?.is_maintenance);
        if (data?.is_server_terminated && data?.expired_date) {
          const expiredDate = startOfTime(
            parseDate(data?.expired_date),
            TimeUnit.DAYS,
          );
          const currentDate = startOfTime(new Date(), TimeUnit.DAYS);
          const diffDays = differenceInTime(
            currentDate,
            expiredDate,
            TimeUnit.DAYS,
          );
          setRemainingDays(diffDays);

          // Grace period: from expired date to 30 days after
          const isInGracePeriod = diffDays >= 0 && diffDays <= 30;

          // Going to expire: within 15 days before the expired date
          const isGoingToExpire = diffDays > -15 && diffDays < 0;

          // Grace period exceeded: more than 30 days after expired date
          const gracePeriodExceeded = diffDays > 30;

          if (gracePeriodExceeded) {
            setIsSubscriptionExpiredModalOpen(true);
          }
          if (isInGracePeriod) {
            setIsSubscriptionGracePeriodModalOpen(true);
          }
          if (isGoingToExpire) {
            setIsSubscriptionExpiringModalOpen(true);
          }
        }
      }
    },
    [],
  );

  const { data, fetch: fetchSubscriptionStatus } = useGet(
    'subscription_status',
    {
      skipToken: true,
      customSkipFn: ({ query }: { query: { tenant_id: string } }) =>
        !query.tenant_id && !user,
      query: {
        version: '0.2',
      },
      onSuccess: handleSubscriptionStatus,
    },
  ) as unknown as {
    data: { expired_date: string; organization_title: string };
    fetch: (config?: { query: { tenant_id: string; version: string } }) => void;
  };

  useEffect(() => {
    const checkTenant = () => {
      const currentTenantId = tenant;
      if (currentTenantId !== tenantId) {
        setTenantId(currentTenantId);
      }
    };
    checkTenant();
    const intervalId = setInterval(checkTenant, 1000);
    return () => clearInterval(intervalId);
  }, [tenantId, tenant]);

  useEffect(() => {
    if (tenantId && token) {
      fetchSubscriptionStatus({
        query: { tenant_id: tenantId, version: '0.2' },
      });
    }
  }, [tenantId, fetchSubscriptionStatus, token]);

  const formattedExpiredDate = formatDate(
    new Date(data?.expired_date ?? new Date()),
    'dd/MM/yyyy',
  );
  const daysRemaining = 30 - Number(remainingDays);
  const gracePeriodDaysRemaining =
    daysRemaining === 1 ? `${daysRemaining} day` : `${daysRemaining} days`;

  return (
    <>
      <ConfirmationModal
        openModal={isMaintenanceModalOpen}
        title={
          <Typography variant="subtitle2" sx={classes.alertTitle}>
            Alert!
          </Typography>
        }
        className={classes.dialogStyle as unknown as string}
        firstButton={null}
        deleteButtonLabel="Log out"
        content={
          <>
            <Typography variant="subtitle2" sx={classes.descriptionTitle}>
              Maintainance Break
            </Typography>
            <Typography sx={classes.descriptionSubtitle}>
              This is a friendly reminder that we have scheduled maintenance for
              Varicon today . During this maintenance window, our systems will
              be temporarily unavailable. We apologize for any inconvenience
              this may cause and appreciate your understanding.
            </Typography>
          </>
        }
        isFirstButtonHidden
        isSecondButtonHidden
        isCloseHidden
        isDivider
        maxWidth="sm"
        handleDelete={() => {}}
      />
      <ConfirmationModal
        openModal={isSubscriptionExpiredModalOpen}
        title={
          <Typography variant="subtitle2" sx={classes.alertTitle}>
            Alert!
          </Typography>
        }
        className={classes.dialogStyle as unknown as string}
        firstButton={null}
        deleteButtonLabel="Log out"
        // enableEmailLink
        content={
          <>
            <Typography variant="subtitle2" sx={classes.descriptionTitle}>
              Subscription Expired
            </Typography>
            <Typography sx={classes.descriptionSubtitle}>
              {data?.organization_title}&apos;s Varicon subscription has expired
              on {formattedExpiredDate}, and the 30-day grace period has also
              ended. Access to Varicon is no longer possible.
              <br />
              <br />
              Please contact your company administrator or Varicon Support to
              renew your subscription and restore access.
            </Typography>
            <Typography>
              For support, reach out to us at{' '}
              <a
                href="mailto:support@varicon.com.au"
                style={classes.emailLink}
                target="_blank"
                rel="noreferrer"
              >
                support@varicon.com.au
              </a>
            </Typography>
          </>
        }
        icon={<EventBusyIcon sx={classes.iconStyle} />}
        isFirstButtonHidden
        isCloseHidden
        isDivider
        maxWidth="sm"
        handleDelete={() => {
          logout();
        }}
      />
      {canManageOrganisationPermission && (
        <WarningModal
          isOpen={isSubscriptionGracePeriodModalOpen}
          handleClose={() => {
            handleSubscriptionGracePeriodModalClose();
          }}
          dialogTitle="Information"
          dialogContent={
            <Stack spacing={2}>
              <Typography sx={classes.subtitle2}>
                Subscription Grace Period!
              </Typography>
              <Typography sx={classes.body}>
                {data?.organization_title}&apos;s Varicon subscription has
                expired on {formattedExpiredDate}. As a courtesy, we have
                extended your access for an additional 30 days. After this grace
                period ends, you will no longer be able to use Varicon.
              </Typography>
              <Typography sx={classes.body}>
                <span style={{ ...classes.body, ...classes.boldFontWeight }}>
                  {gracePeriodDaysRemaining}
                </span>{' '}
                remaining on your Grace Period. Renew now to maintain
                uninterrupted access
              </Typography>
              <Typography sx={{ ...classes.body, ...classes.secondaryText }}>
                For more information about your subscription status, please
                reach out to your company administrator or contact Varicon
                Support at{' '}
                <a
                  href="mailto:support@varicon.com.au"
                  style={classes.emailLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  support@varicon.com.au
                </a>
              </Typography>
            </Stack>
          }
          showDialogActions={false}
        />
      )}

      {canManageOrganisationPermission && (
        <Snackbar
          open={isSubscriptionExpiringModalOpen}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            width: '100%',
            paddingTop: '1rem',
            '& .MuiSnackbarContent-root': {
              backgroundColor: secondary.background,
            },
          }}
          message={
            <Alert
              severity="warning"
              onClose={() => {
                setIsSubscriptionExpiringModalOpen(false);
              }}
            >
              <span style={{ ...classes.body, ...classes.boldFontWeight }}>
                {`${Math.abs(remainingDays)} ${
                  Math.abs(remainingDays) === 1 ? 'day' : 'days'
                } `}
              </span>
              <span style={classes.body}>
                remaining on your subscription. Please contact Varicon Support
                to extend your subscription.
              </span>
              <a
                href="mailto:support@varicon.com.au"
                style={classes.emailLink}
                target="_blank"
                rel="noreferrer"
              >
                {' '}
                Contact Varicon Support
              </a>
            </Alert>
          }
        />
      )}
    </>
  );
};
